import {
  FEATURE_FLAG,
  isValidSubscription,
  UserProfile,
} from "@blacknut/javascript-sdk/dist";
import { logD, logE } from "@blacknut/logging/dist";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDeeplink } from "../redux/actions/App";
import { getDeviceStreams, streamStopped } from "../redux/actions/Game";
import {
  dismissFamilyInfoLoadError,
  loadFamilyInfo,
  selectProfile,
} from "../redux/actions/Profiles";
import { dismissAuthenticationError } from "../redux/actions/User";
import { State } from "../redux/reducers";
import { RouteNames, WithRouterProps } from "../utils/WithRouter";
const TAG = "Profiles";

//FIXME distinguish between returnUrl&deeplinkUrl
//In case of returnUrl honor active streams
//In case of deeplink cancel active streams
export const useProfilesPage = (
  props: WithRouterProps<{ returnUrl?: string }> & { returnUrl?: string },
) => {
  const { familyProfiles, profile } = useSelector((state: State) => state.profilesState);
  const { authentication, familyToken, user } = useSelector(
    (state: State) => state.globalState,
  );
  const { error: authenticationError, inProgress: authenticating } = authentication;
  const { audid, deeplink, config } = useSelector((state: State) => state.globalState);

  const featureFlags = config?.features || {};
  const allowNoSubscription =
    featureFlags[FEATURE_FLAG.ALLOW_NAVIGATION_SUBSCRIPTION_KO];

  const dispatch = useDispatch();

  React.useEffect(() => {
    const init = async () => {
      if (
        (props.isScreenForeground ?? true) &&
        !familyProfiles.profiles &&
        familyToken
      ) {
        try {
          await loadFamilyInfo()(dispatch);
        } catch (e) {
          logE(TAG, "Caught error loading profiles", e);
        }
      }
    };
    init();
  }, [dispatch, familyProfiles.profiles, familyToken, props.isScreenForeground]);

  const _onSelectProfile = React.useCallback(
    async (aProfile: UserProfile, redirectUrl?: string) => {
      if (profile && profile.id === aProfile.id) {
        logD(TAG, "Selecting same profile");
        let returnUrl = redirectUrl ?? props.returnUrl;

        if (!returnUrl && props.routeParams) {
          returnUrl = props.routeParams.returnUrl;
        }
        if (returnUrl) {
          props.navigate(returnUrl as string);
          if (deeplink) {
            setDeeplink()(dispatch);
          }
        } else {
          props.navigate({ name: RouteNames.HOME, clearHistory: true });
        }
      } else if (aProfile.isPinLocked) {
        let returnUrl = redirectUrl ?? props.returnUrl;
        if (!returnUrl && props.routeParams) {
          returnUrl = props.routeParams.returnUrl;
        }
        props.navigate({
          name: RouteNames.PROFILE_UNLOCK,
          id: aProfile.id,
          returnUrl,
        });
      } else {
        try {
          const {
            profile: newProfile,
            user: newUser,
            billingInfo,
            activeStreams,
          } = await selectProfile(aProfile, null, true)(dispatch);
          const deviceStreams =
            activeStreams &&
            activeStreams.user &&
            getDeviceStreams(activeStreams.user, audid || "");

          let returnUrl = redirectUrl || deeplink || props.returnUrl;
          if (!returnUrl && props.routeParams) {
            returnUrl = props.routeParams.returnUrl;
          }

          // User selected a profile
          if (returnUrl) {
            // open via deeplink, cancel all active streams if any

            if (deviceStreams && deviceStreams.length > 0) {
              deviceStreams.map((u) => {
                streamStopped(u.streamId, "Canceled", undefined, false)(dispatch);
              });
            }
            props.navigate(returnUrl as string);
            if (deeplink) {
              setDeeplink()(dispatch);
            }
          } else {
            if (
              newUser &&
              newProfile.isMaster &&
              !isValidSubscription(newUser, billingInfo) &&
              !allowNoSubscription
            ) {
              props.navigate({
                name: RouteNames.PAYMENT_SELECTION,
                clearHistory: true,
              });
              return;
            }

            if (deviceStreams && deviceStreams.length > 0) {
              props.navigate({
                name: RouteNames.STREAM_RECOVERY,
                clearHistory: true,
              });
            } else {
              props.navigate({ name: RouteNames.HOME, clearHistory: true });
            }
          }
        } catch (e) {
          logE(TAG, "Caught error selecting profile", e);
        }
      }
    },
    [allowNoSubscription, audid, deeplink, dispatch, profile, props],
  );

  return {
    profiles: familyProfiles.profiles,
    profile,
    loading: familyProfiles.inProgress,
    loadingError: familyProfiles.error,
    dismissLoadingError: React.useCallback(() => {
      dismissFamilyInfoLoadError()(dispatch);
    }, [dispatch]),
    dismissAuthenticationError: React.useCallback(() => {
      dismissAuthenticationError()(dispatch);
    }, [dispatch]),
    authenticating,
    authenticationError,
    user,
    onSelectProfile: _onSelectProfile,
  };
};
